import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  CardHeaderToolbar,
} from "../../../../_metronic/_partials/controls";
import Table, {
  dateFormatter,
  buttonsStyle,
} from "../../../components/tables/table";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import {
  getNoticias,
  deleteNoticia,
  changeStatusNoticia,
  duplicarNoticia,
} from "../../../../api/noticia";
import {
  Button,
  Tooltip,
  Switch,
  FormControlLabel,
  TextField,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import { Autorenew as FilterOffIcon } from "@material-ui/icons";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import { alertError, alertSuccess } from "../../../../utils/logger";
import { useHistory } from "react-router-dom";
import ToggleOffIcon from "@material-ui/icons/ToggleOff";
import ToggleOnIcon from "@material-ui/icons/ToggleOn";
import { shallowEqual, useSelector } from "react-redux";
import { canEditAdminContent } from "../../../../_helpers/backofficePermissions";
import FiltersCard from "../../../components/filters/Filter";
import { SERVER_URL } from "../../../../api";
import { getCategorias } from "../../../../api/categoria";
import ImageCarouselDialog from "../../../components/dialogs/ImageCarouselDialog";
import { getNonEmpty } from "../../../../utils/helpers";
import PreviewDialog from "../../../components/dialogs/PreviewDialog";
import { Copy } from "lucide-react";

function getData(noticias, categorias) {
  if (!noticias || !Array.isArray(noticias)) return [];
  if (!categorias || !Array.isArray(categorias)) categorias = [];
  
  let data = [];
  for (let i = 0; i < noticias.length; ++i) {
    const elem = {};
    elem.titulo = getNonEmpty(noticias[i].titulo);
    elem.createdAt = noticias[i].createdAt;
    elem.fechaPublicacion =
      noticias[i].fechaPublicacion || noticias[i].createdAt;
    elem.imageURL = noticias[i].imageURL;

    // Manejar categoria: puede venir como objeto populado o como string ID
    const categoriaId =
      typeof noticias[i].cate === "object" && noticias[i].cate && noticias[i].cate._id
        ? noticias[i].cate._id.toString()
        : noticias[i].cate ? noticias[i].cate.toString() : null;

    if (categoriaId && categorias.length > 0) {
      const categoriaEncontrada = categorias.find((x) => x && x._id && x._id.toString() === categoriaId);
      elem.cate = getNonEmpty(categoriaEncontrada ? categoriaEncontrada.titulo : null);
    } else {
      elem.cate = "";
    }

    elem.id = noticias[i]._id;
    elem.destacado = noticias[i].destacado;
    elem.active = noticias[i].active;
    data = data.concat(elem);
  }
  return data;
}

const initialFilters = {
  active: false,
};

export default function NoticiasPage() {
  const [data, setData] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [noticiaId, setNoticiaId] = useState(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(null);
  const [refresh, setRefresh] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [collapsed, setCollapsed] = useState(true);
  // Cargar filtros desde localStorage al iniciar
  const [filterOptions, setFilterOptions] = useState(() => {
    const savedFilters = localStorage.getItem("noticiasPageFilters");
    return savedFilters ? JSON.parse(savedFilters) : initialFilters;
  });
  // Estado para el campo de búsqueda
  const [searchText, setSearchText] = useState(() => {
    const saved = localStorage.getItem("noticiasPage_searchText");
    return saved || "";
  });
  const history = useHistory();
  const user = useSelector((store) => store.authentication?.user, shallowEqual);
  const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
  const [previewImages, setPreviewImages] = useState(null);

  function buttonFormatter(cell) {
    const elem = data.find((item) => item._id === cell);
    return (
      <>
        <Tooltip title="Edit">
          <Button
            style={buttonsStyle}
            size="small"
            onClick={() => {
              // Guardar filtros actuales antes de navegar
              localStorage.setItem(
                "noticiasPageFilters",
                JSON.stringify(filterOptions)
              );
              history.push("/edit-noticia/" + cell);
            }}
          >
            <EditIcon />
          </Button>
        </Tooltip>
        {canEditAdminContent(user?.role) && (
          <>
            <Tooltip title="Duplicar">
              <Button
                style={buttonsStyle}
                size="small"
                onClick={() => {
                  setNoticiaId(elem);
                  setOpenConfirmDialog(4);
                }}
              >
                <Copy />
              </Button>
            </Tooltip>
            <Tooltip title={elem?.active ? "Disable" : "Enable"}>
              <Button
                style={buttonsStyle}
                size="small"
                onClick={() => {
                  setNoticiaId(elem);
                  setOpenConfirmDialog(1);
                }}
              >
                {elem?.active ? (
                  <ToggleOffIcon />
                ) : (
                  <ToggleOnIcon style={{ color: "red" }} />
                )}
              </Button>
            </Tooltip>
            <Tooltip title="Delete">
              <Button
                style={buttonsStyle}
                size="small"
                onClick={() => {
                  setNoticiaId(cell);
                  setOpenConfirmDialog(2);
                }}
              >
                <DeleteIcon />
              </Button>
            </Tooltip>
          </>
        )}
      </>
    );
  }

  function destacadoFormatter(cell) {
    return cell ? "Sí" : "No";
  }

  function imageFormatter(imageURL) {
    return imageURL ? (
      <img
        src={SERVER_URL + "/" + imageURL}
        alt="artwork"
        style={{ width: "100px", cursor: "zoom-in" }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setPreviewImages(imageURL);
          setOpenPreviewDialog(1);
        }}
      />
    ) : (
      <div />
    );
  }

  const columns = [
    { dataField: "titulo", text: "Titulo", sort: true },
    {
      dataField: "fechaPublicacion",
      text: "Fecha de Publicación",
      sort: true,
      formatter: dateFormatter,
    },
    {
      dataField: "destacado",
      text: "Destacado",
      sort: true,
      formatter: destacadoFormatter,
    },
    { dataField: "imageURL", text: "Imagen", formatter: imageFormatter },
    { dataField: "cate", text: "Categoria", sort: true },
    { dataField: "id", text: "", formatter: buttonFormatter },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const resCategorias = await getCategorias();
        if (resCategorias.status === 200) {
          setCategorias(resCategorias.data);
        }
        getNoticias()
          .then((res) => {
            if (res.status === 200) {
              setData(res.data);
              // Aplicar filtros guardados si existen
              const savedFilters = localStorage.getItem("noticiasPageFilters");
              if (savedFilters) {
                const savedFilterOptions = JSON.parse(savedFilters);
                setFilterOptions(savedFilterOptions);
                // Aplicar filtros automáticamente
                const filtered = res.data.filter((item) => {
                  let filter = true;
                  if (savedFilterOptions.active) filter = filter && item.active;
                  if (filter) return item;
                  return false;
                });
                setFilteredData(filtered);
              } else {
                setFilteredData(res.data);
              }
              setRefresh(false);
            }
          })
          .catch((error) => {
            alertError({
              error: error,
              customMessage: "Could not get noticias.",
            });
          });
      } catch (error) {}
    };

    fetchData();
  }, [refresh]);

  const handleSearch = async () => {
    if (!data.length) return;
    // Guardar filtros en localStorage
    localStorage.setItem("noticiasPageFilters", JSON.stringify(filterOptions));
    setFilteredData(
      data.filter((item) => {
        let filter = true;
        if (filterOptions.active) filter = filter && item.active;
        if (filter) return item;
        return false;
      })
    );
  };

  const handleClearFilters = () => {
    setFilterOptions(initialFilters);
    setSearchText("");
    localStorage.removeItem("noticiasPageFilters");
    localStorage.removeItem("noticiasPage_searchText");
    localStorage.removeItem("noticiasPage_currentPage");
    setRefresh(true);
  };

  const renderFiltersContent = () => {
    return (
      <div style={{ padding: "15px 0 0 0" }}>
        {/* Primera fila: Campo de búsqueda */}
        <div className="row align-items-end">
          <div className="col-md-3 col-sm-6 mb-3">
            <TextField
              id="search"
              label="Buscar"
              placeholder="Buscar en todos los campos..."
              value={searchText}
              onChange={(e) => {
                const value = e.target.value;
                setSearchText(value);
                if (value.trim()) {
                  localStorage.setItem("noticiasPage_searchText", value);
                } else {
                  localStorage.removeItem("noticiasPage_searchText");
                }
              }}
              margin="dense"
              variant="outlined"
              fullWidth
              style={{ marginTop: 0, marginBottom: 0 }}
            />
          </div>
        </div>
        {/* Switches y botones de acción en la misma línea */}
        <div className="row">
          <div className="col-12">
            <div
              className="d-flex justify-content-between align-items-center flex-wrap"
              style={{ marginTop: "10px", marginBottom: "10px", gap: "15px" }}
            >
              {/* Switches a la izquierda */}
              <div
                className="d-flex align-items-center flex-wrap"
                style={{ gap: "30px" }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={filterOptions.active}
                      onChange={() =>
                        setFilterOptions({
                          ...filterOptions,
                          active: !filterOptions.active,
                        })
                      }
                      name="checkActive"
                      style={{ color: "#f3691a" }}
                    />
                  }
                  label={
                    filterOptions.active
                      ? "Solo activos"
                      : "Todos (activos/inactivos)"
                  }
                  style={{ margin: 0 }}
                />
              </div>
              {/* Botones a la derecha */}
              <div className="d-flex">
                <Button
                  variant="contained"
                  onClick={handleSearch}
                  startIcon={<SearchIcon />}
                  style={{
                    minWidth: "120px",
                    backgroundColor: "#f3691a",
                    color: "#fff",
                    marginRight: "10px",
                  }}
                >
                  Buscar
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleClearFilters}
                  startIcon={<FilterOffIcon />}
                  style={{
                    minWidth: "120px",
                    borderColor: "#f3691a",
                    color: "#f3691a",
                  }}
                >
                  Limpiar
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <Card>
        <CardHeader title="Lista Categorías">
          <CardHeaderToolbar>
            <Button
              variant="contained"
              onClick={() => history.push("/edit-noticia")}
              startIcon={<AddIcon />}
              style={{
                backgroundColor: "#f3691a",
                color: "#fff",
              }}
            >
              Añadir nuevo
            </Button>
          </CardHeaderToolbar>
        </CardHeader>
        <CardBody>
          {/* Filtros siempre visibles con mejor diseño */}
          <Card style={{ marginBottom: "15px", backgroundColor: "#f8f9fa" }}>
            <CardBody style={{ padding: "15px 20px" }}>
              {renderFiltersContent()}
            </CardBody>
          </Card>

          <Table
            data={getData(filteredData, categorias)}
            columns={columns}
            pageKey="noticiasPage"
            searchText={searchText}
            sizePerPage={100}
            onSearchChange={(value) => {
              setSearchText(value);
              if (value.trim()) {
                localStorage.setItem("noticiasPage_searchText", value);
              } else {
                localStorage.removeItem("noticiasPage_searchText");
              }
            }}
          />

          <ConfirmDialog
            title={`Are you sure you want to ${
              noticiaId?.active ? "disable" : "enable"
            } this user type?`}
            open={openConfirmDialog === 1}
            setOpen={setOpenConfirmDialog}
            onConfirm={() => {
              changeStatusNoticia(noticiaId._id, !noticiaId?.active)
                .then((res) => {
                  if (res.status === 200) {
                    alertSuccess({
                      title: `${noticiaId?.active ? "Disabled!" : "Enabled!"}`,
                      customMessage: `User type ${
                        noticiaId?.active ? "disabled" : "enabled"
                      } successfully`,
                    });
                    setRefresh(true);
                  }
                })
                .catch((error) => {
                  alertError({
                    error: error,
                    customMessage: `Could not ${
                      noticiaId?.active ? "disable" : "enable"
                    } user type.`,
                  });
                });
            }}
          />
          <ConfirmDialog
            title={"Are you sure you want to remove this news?"}
            open={openConfirmDialog === 2}
            setOpen={setOpenConfirmDialog}
            onConfirm={() => {
              deleteNoticia(noticiaId)
                .then((res) => {
                  if (res.status === 204 || res.status === 200) {
                    alertSuccess({
                      title: "Deleted!",
                      customMessage: "Noticia removed successfully.",
                    });
                    setRefresh(true);
                  }
                })
                .catch((error) => {
                  alertError({
                    error: error,
                    customMessage: "Could not remove noticia.",
                  });
                });
            }}
          />
          <PreviewDialog
            title={"Preview"}
            open={openPreviewDialog === 1}
            setOpen={setOpenPreviewDialog}
            file={previewImages}
            src={previewImages ? SERVER_URL + "/" + previewImages : undefined}
          />
          <ConfirmDialog
            title={`Estas seguro que quieres duplicar esta noticia?`}
            open={openConfirmDialog === 4}
            setOpen={setOpenConfirmDialog}
            onConfirm={() => {
              duplicarNoticia(noticiaId._id, noticiaId)
                .then((res) => {
                  if (res.status === 201) {
                    alertSuccess({
                      title: `Duplicado`,
                      customMessage: `Noticia duplicado`,
                    });
                    setRefresh(true);
                  }
                })
                .catch((error) => {
                  alertError({
                    error: error,
                    customMessage: `No se ha podido duplicar la noticia.`,
                  });
                });
            }}
          />
        </CardBody>
      </Card>
    </>
  );
}
